import React, { useState } from 'react';
import {
  App as AntApp,
  Avatar,
  Button,
  Card,
  Flex,
  Form,
  InputNumber,
  Select,
  Tag,
  Typography,
} from 'antd';
import { CloudSyncOutlined, LogoutOutlined, UserOutlined } from '@ant-design/icons';
import type { User } from '@supabase/supabase-js';
import { TRANSLATION_LANGUAGES, TranslationLanguage } from '@lingoping/core';
import type { AccessState, Profile } from '@lingoping/core';
import { StorageManager } from '../storage/index.js';
import { platform } from '../platform.js';
import { NativeLanguageName } from '../components/TranslationText.js';
import {
  baseTranslationLanguage,
  saveTranslationLanguage,
} from '../services/translationSettings.js';
import {
  MAX_NEW_WORDS_PER_DAY,
  MIN_NEW_WORDS_PER_DAY,
  newWordsPerDay,
} from '../services/newWordsSettings.js';
import { AccountActions } from '../components/billing/AccountActions.js';
import { SubscriptionCard } from '../components/billing/SubscriptionCard.js';
import { tourTarget } from './tourTargets.js';

const { Text, Paragraph } = Typography;

const INTERVAL_OPTIONS = [
  { value: 5, label: 'Every 5 minutes' },
  { value: 10, label: 'Every 10 minutes' },
  { value: 15, label: 'Every 15 minutes' },
  { value: 30, label: 'Every 30 minutes' },
  { value: 60, label: 'Every hour' },
];

const TRANSLATION_LANGUAGE_OPTIONS = TRANSLATION_LANGUAGES.map((info) => ({
  value: info.code,
  label: (
    <span>
      <NativeLanguageName lang={info.code} />
      {info.nativeName !== info.name && <span style={{ color: '#5b6472' }}> · {info.name}</span>}
    </span>
  ),
}));

const CARDS_PER_REVIEW_OPTIONS = [
  { value: 3, label: '3 cards' },
  { value: 5, label: '5 cards (recommended)' },
  { value: 7, label: '7 cards' },
  { value: 10, label: '10 cards' },
];

interface FormValues {
  session_interval_minutes: number;
  cards_per_session: number;
  new_words_per_day: number;
}

interface Props {
  profile: Profile;
  authUser: User | null;
  onSaved: (profile: Profile) => void;
  onSignOut: () => Promise<void>;
  /** The server's access answer; the Subscription card only shows once payments are on. */
  access?: AccessState | null;
  refreshAccess?: () => Promise<AccessState | null>;
}

/** Two cards side by side on a wide screen, one under the other on a narrow one. */
const TWO_COLUMNS: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 380px), 1fr))',
  gap: 16,
  alignItems: 'stretch',
};

export const SettingsTab: React.FC<Props> = ({
  profile,
  authUser,
  onSaved,
  onSignOut,
  access,
  refreshAccess,
}) => {
  const { message } = AntApp.useApp();
  const [saving, setSaving] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [savingLanguage, setSavingLanguage] = useState(false);

  // Applied as soon as it is picked (no Save needed): every card then shows this language.
  const handleLanguageChange = async (language: TranslationLanguage) => {
    setSavingLanguage(true);
    try {
      onSaved(await saveTranslationLanguage(language));
      message.success('Translations will now be shown in your new language.');
    } catch (err) {
      console.error('Failed to save the translation language:', err);
      message.error("Your language couldn't be saved. Please try again.");
    } finally {
      setSavingLanguage(false);
    }
  };

  const handleSave = async (values: FormValues) => {
    setSaving(true);
    try {
      // Saved here at once and in the account with the next sync, like the language.
      onSaved(
        await StorageManager.saveSettings({
          session_interval_minutes: values.session_interval_minutes,
          cards_per_session: values.cards_per_session,
          new_words_per_day: values.new_words_per_day ?? newWordsPerDay(profile),
        })
      );
      message.success('Settings saved.');

      platform.notifyBackground({ type: 'UPDATE_ALARM' });
    } catch (err) {
      console.error('Failed to save settings:', err);
      message.error("Your settings couldn't be saved. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      await onSignOut();
    } finally {
      setSigningOut(false);
    }
  };

  const displayName = authUser?.user_metadata?.full_name || authUser?.email;

  return (
    <Flex vertical gap={16}>
      <div style={TWO_COLUMNS}>
        <Card title="Your account">
          {authUser ? (
            <Flex justify="space-between" align="center" wrap gap={16}>
              <Flex align="center" gap={12} style={{ minWidth: 0 }}>
                <Avatar src={authUser.user_metadata?.avatar_url} icon={<UserOutlined />} />
                <Flex vertical style={{ minWidth: 0 }}>
                  <Text strong ellipsis>
                    {displayName}
                  </Text>
                  {authUser.email && displayName !== authUser.email && (
                    <Text type="secondary" ellipsis>
                      {authUser.email}
                    </Text>
                  )}
                </Flex>
              </Flex>
              <Button icon={<LogoutOutlined />} loading={signingOut} onClick={handleSignOut}>
                Sign out
              </Button>
            </Flex>
          ) : (
            <Text type="secondary">You are not signed in.</Text>
          )}
          <Flex align="center" gap={8} style={{ marginTop: 16 }}>
            <CloudSyncOutlined style={{ color: '#15803d' }} aria-hidden="true" />
            <Text type="secondary">Everything is saved to your account.</Text>
          </Flex>
          <div style={{ marginTop: 16 }}>
            <AccountActions />
          </div>
        </Card>

        <Card title="Language">
          <Flex vertical gap={16}>
            <Flex align="center" gap={12} wrap>
              <Text style={{ width: 160, color: '#475569' }}>You&apos;re learning</Text>
              <Tag color="blue" style={{ margin: 0 }}>
                German
              </Tag>
            </Flex>
            <Flex align="start" gap={12} wrap>
              <label
                htmlFor="translation-language"
                style={{ width: 160, paddingTop: 5, color: '#475569' }}
              >
                Translations in
              </label>
              <Flex vertical gap={6} style={{ flex: '1 1 280px', maxWidth: 440 }}>
                <Select<TranslationLanguage>
                  id="translation-language"
                  value={baseTranslationLanguage(profile)}
                  options={TRANSLATION_LANGUAGE_OPTIONS}
                  loading={savingLanguage}
                  onChange={handleLanguageChange}
                  style={{ width: 280 }}
                />
                <Text type="secondary" style={{ fontSize: 13 }}>
                  Meanings and examples are shown in this language.
                </Text>
              </Flex>
            </Flex>
            <Flex align="center" gap={12} wrap>
              <Text style={{ width: 160, color: '#475569' }}>App language</Text>
              <Text>English</Text>
              <Text type="secondary" style={{ fontSize: 13 }}>
                Menus and buttons
              </Text>
            </Flex>
          </Flex>
        </Card>

        {access?.enforced && (
          <SubscriptionCard access={access} refreshAccess={refreshAccess ?? (async () => null)} />
        )}
      </div>

      <Form<FormValues>
        layout="vertical"
        requiredMark={false}
        initialValues={{
          session_interval_minutes: profile.session_interval_minutes,
          cards_per_session: profile.cards_per_session,
          new_words_per_day: newWordsPerDay(profile),
        }}
        onFinish={handleSave}
      >
        <Flex vertical gap={16}>
          <div style={TWO_COLUMNS}>
            <Card title="Review reminders">
              <Paragraph type="secondary">Short reminders to review a few cards.</Paragraph>
              <Flex gap={16} wrap>
                <div style={{ flex: '1 1 220px' }} {...tourTarget('remind')}>
                  <Form.Item
                    name="session_interval_minutes"
                    label="Remind me"
                    rules={[{ required: true }]}
                    style={{ marginBottom: 0 }}
                  >
                    <Select options={INTERVAL_OPTIONS} />
                  </Form.Item>
                </div>
                <div style={{ flex: '1 1 220px' }} {...tourTarget('per-review')}>
                  <Form.Item
                    name="cards_per_session"
                    label="Cards per review"
                    rules={[{ required: true }]}
                    style={{ marginBottom: 0 }}
                  >
                    <Select options={CARDS_PER_REVIEW_OPTIONS} />
                  </Form.Item>
                </div>
              </Flex>
            </Card>

            <Card title="New words">
              <Paragraph type="secondary">
                The most new words you start per day. Reviews are not affected.
              </Paragraph>
              <div style={{ display: 'inline-block' }} {...tourTarget('new-words')}>
                <Form.Item
                  name="new_words_per_day"
                  label="New words per day"
                  extra={`Between ${MIN_NEW_WORDS_PER_DAY} and ${MAX_NEW_WORDS_PER_DAY}. The default is 50.`}
                  rules={[
                    {
                      required: true,
                      type: 'integer',
                      min: MIN_NEW_WORDS_PER_DAY,
                      max: MAX_NEW_WORDS_PER_DAY,
                      message: `Enter a whole number from ${MIN_NEW_WORDS_PER_DAY} to ${MAX_NEW_WORDS_PER_DAY}.`,
                    },
                  ]}
                  style={{ marginBottom: 0 }}
                >
                  <InputNumber
                    min={MIN_NEW_WORDS_PER_DAY}
                    max={MAX_NEW_WORDS_PER_DAY}
                    precision={0}
                    style={{ width: 120 }}
                  />
                </Form.Item>
              </div>
            </Card>
          </div>

          <div>
            <Button type="primary" htmlType="submit" loading={saving}>
              Save settings
            </Button>
          </div>
        </Flex>
      </Form>
      {platform.SettingsExtras && <platform.SettingsExtras />}
    </Flex>
  );
};
